import {
  Box,
  Card,
  Checkbox,
  Divider,
  Grid,
  Icon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Controller, FormProvider, useForm } from "react-hook-form";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import MDDatePicker from "components/MDDatePicker";
import { GenerateReportMutationVariables, ReportType } from "generated/graphql";
import { capitalCase } from "change-case";
import { useEffect, useMemo, useRef, useState } from "react";
import { kebabCase } from "lodash";
import useGenerateReport from "hooks/organization/reports/useGenerateReport";
import DataTable from "components/Tables/DataTable/DataTable";
import useGetReportJobDetails from "hooks/organization/reports/types/useGetReportJobDetails";
import useTableJobDetails from "hooks/organization/reports/types/useReportTableJobDetails";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import JobOptions from "modules/jobs/JobOptions";
import CompanyOptions from "modules/companies/CompanyOptions";
import { useGetCompanyLazy } from "hooks/companies/useGetCompanyLazy";
import { zodResolver } from "@hookform/resolvers/zod";
import { createProposalSchema } from "DDD/action-objects/ProposalCreate";
import { z } from "zod";
import dayjs from "dayjs";

// Extended schema for job filter
const reportFilterSchema = createProposalSchema.extend({
  jobId: z.string().optional(),
});

type ReportFilterFormData = z.infer<typeof reportFilterSchema>;

const getReportDefaultValues = (
  initialValues: Partial<ReportFilterFormData>
): Partial<ReportFilterFormData> => ({
  companyId: "",
  contactId: "",
  source: undefined,
  jobId: "",
  ...initialValues,
});

const ReportJobDetails = () => {
  const {
    handleSubmit,
    formState: { errors },
  } = useForm();

  let today = dayjs().format("YYYY-MM-DD 00:00:00");

  // Set start date to 1 month ago
  const startDateFormatted = dayjs().subtract(1, "month").format("YYYY-MM-DD 00:00:00");

  const [date, setDate] = useState({ startDate: startDateFormatted, endDate: today });
  const [generateReport, { data: generateReportData }] = useGenerateReport();
  const [anchorEl, setAnchorEl] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [jobId, setJobId] = useState("");
  const [jobName, setJobName] = useState("");
  const [triggerRefetch, setTriggerRefetch] = useState(false);

  // Separate hook instances: one for downloads (with toast), one for regular operations (no toast)
  const [getReportJobDetailsForDownload] = useGetReportJobDetails(true); // Shows success message for downloads
  const [getCompany, { company }] = useGetCompanyLazy();

  const companyInitialId = null;

  const methods = useForm<ReportFilterFormData>({
    resolver: zodResolver(reportFilterSchema),
    defaultValues: getReportDefaultValues({ companyId: companyInitialId }),
  });

  const companyId = methods.watch("companyId");

  const {
    tableData: originalTableData,
    pagination,
    loading,
    getReportJobDetails,
    metadata,
  } = useTableJobDetails(date.startDate, date.endDate, companyId, jobId);

  // Format currency for display
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const tableData = {
    ...originalTableData,
    rows: [
      ...(originalTableData?.rows || []),
      // Add grand total row if we're on the last page and have metadata
      ...(pagination?.hasNextPage === false && metadata?.totals?.proposed_overall_total
        ? [
            {
              job_external_id: "Total",
              actual_overall_total: metadata.totals.actual_overall_total,
              proposed_overall_total: metadata.totals.proposed_overall_total,
              style: {
                fontWeight: "bold",
                backgroundColor: "#f5f5f5",
                borderTop: "2px solid #ddd",
              },
            },
          ]
        : []),
    ],
  };

  // Initialize column visibility state with specified fields hidden by default
  const [columnVisibility, setColumnVisibility] = useState({
    job_external_id: true,
    job_id: true,
    proposal_external_id: true,
    status: true,
    starts_at: true,
    ends_at: true,
    company_name: true,
    proposal_stage_name: true,
    organization_product_type_name: true,
    notes: true,
    product_name: true,
    proposed_overall_total: true,
    actual_overall_total: true,
    // Hidden by default as specified
    proposal_stage_id: false,
    proposed_cost_material: false,
    actual_cost_material: false,
    proposed_cost_equipment: false,
    actual_cost_equipment: false,
    proposed_cost_labour: false,
    actual_cost_labour: false,
    proposed_cost_additional: false,
    actual_cost_additional: false,
    proposed_cost_direct: false,
    actual_cost_direct: false,
    proposed_cost_overhead: false,
    actual_cost_overhead: false,
    proposed_cost_indirect: false,
    actual_cost_indirect: false,
    proposed_cost_total: false,
    actual_cost_total: false,
    proposed_profit: false,
    actual_profit: false,
    proposed_profit_percentage: false,
    actual_profit_percentage: false,
    proposed_gross_margin: false,
    actual_gross_margin: false,
    proposed_gross_margin_percentage: false,
    actual_gross_margin_percentage: false,
    proposed_operating_margin: false,
    actual_operating_margin: false,
    proposed_operating_margin_percentage: false,
    actual_operating_margin_percentage: false,
    proposed_net_margin: false,
    actual_net_margin: false,
    proposed_net_margin_percentage: false,
    actual_net_margin_percentage: false,
    proposal_stage_address: false,
    proposal_stage_city: false,
    proposal_stage_state: false,
    proposal_stage_zip: false,
    crew_name: false,
    crew_display_name: false,
    proposal_product_job_size: false,
    proposal_product_job_duration: false,
    proposal_product_material_duration: false,
    proposal_product_material_type: false,
    proposal_product_material_used: false,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "Job ID",
        accessor: "job_external_id",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.job_external_id,
      },
      {
        Header: "Job Phase Id",
        accessor: "job_id",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.job_id,
      },
      {
        Header: "Proposal Id",
        accessor: "proposal_external_id",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_external_id,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => value ?? "",
        filter: "includes",
        disableSortBy: true,
        visible: columnVisibility.status,
      },
      {
        Header: "Started at",
        accessor: "starts_at",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.starts_at,
      },
      {
        Header: "Ends at",
        accessor: "ends_at",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.ends_at,
      },
      {
        Header: "Client Name",
        accessor: "company_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.company_name,
      },
      {
        Header: "Job Stage Name",
        accessor: "proposal_stage_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_stage_name,
      },
      {
        Header: "Product Type",
        accessor: "organization_product_type_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.organization_product_type_name,
      },
      {
        Header: "Notes",
        accessor: "notes",
        Cell: ({ value }) => (value ? <div dangerouslySetInnerHTML={{ __html: value }} /> : ""),
        disableSortBy: true,
        visible: columnVisibility.notes,
      },
      // Hidden columns by default
      {
        Header: "Proposal Stage Id",
        accessor: "proposal_stage_id",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_stage_id,
      },
      {
        Header: "Proposed Cost Material",
        accessor: "proposed_cost_material",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_cost_material,
      },
      {
        Header: "Actual Cost Material",
        accessor: "actual_cost_material",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_cost_material,
      },
      {
        Header: "Proposed Cost Equipment",
        accessor: "proposed_cost_equipment",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_cost_equipment,
      },
      {
        Header: "Actual Cost Equipment",
        accessor: "actual_cost_equipment",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_cost_equipment,
      },
      {
        Header: "Proposed Cost Labor",
        accessor: "proposed_cost_labour",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_cost_labour,
      },
      {
        Header: "Actual Cost Labor",
        accessor: "actual_cost_labour",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_cost_labour,
      },
      {
        Header: "Proposed Cost Additional",
        accessor: "proposed_cost_additional",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_cost_additional,
      },
      {
        Header: "Actual Cost Additional",
        accessor: "actual_cost_additional",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_cost_additional,
      },
      {
        Header: "Proposed Cost Direct",
        accessor: "proposed_cost_direct",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_cost_direct,
      },
      {
        Header: "Actual Cost Direct",
        accessor: "actual_cost_direct",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_cost_direct,
      },
      {
        Header: "Proposed Cost Overhead",
        accessor: "proposed_cost_overhead",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_cost_overhead,
      },
      {
        Header: "Actual Cost Overhead",
        accessor: "actual_cost_overhead",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_cost_overhead,
      },
      {
        Header: "Proposed Cost Indirect",
        accessor: "proposed_cost_indirect",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_cost_indirect,
      },
      {
        Header: "Actual Cost Indirect",
        accessor: "actual_cost_indirect",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_cost_indirect,
      },
      {
        Header: "Proposed Cost Total",
        accessor: "proposed_cost_total",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_cost_total,
      },
      {
        Header: "Actual Cost Total",
        accessor: "actual_cost_total",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_cost_total,
      },
      {
        Header: "Proposed Profit",
        accessor: "proposed_profit",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_profit,
      },
      {
        Header: "Actual Profit",
        accessor: "actual_profit",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_profit,
      },
      {
        Header: "Proposed Profit Percentage",
        accessor: "proposed_profit_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_profit_percentage,
      },
      {
        Header: "Actual Profit Percentage",
        accessor: "actual_profit_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_profit_percentage,
      },
      {
        Header: "Proposed Gross Margin",
        accessor: "proposed_gross_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_gross_margin,
      },
      {
        Header: "Actual Gross Margin",
        accessor: "actual_gross_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_gross_margin,
      },
      {
        Header: "Proposed Gross Margin Percentage",
        accessor: "proposed_gross_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_gross_margin_percentage,
      },
      {
        Header: "Actual Gross Margin Percentage",
        accessor: "actual_gross_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_gross_margin_percentage,
      },
      {
        Header: "Proposed Operating Margin",
        accessor: "proposed_operating_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_operating_margin,
      },
      {
        Header: "Actual Operating Margin",
        accessor: "actual_operating_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_operating_margin,
      },
      {
        Header: "Proposed Operating Margin Percentage",
        accessor: "proposed_operating_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_operating_margin_percentage,
      },
      {
        Header: "Actual Operating Margin Percentage",
        accessor: "actual_operating_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_operating_margin_percentage,
      },
      {
        Header: "Proposed Net Margin",
        accessor: "proposed_net_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_net_margin,
      },
      {
        Header: "Actual Net Margin",
        accessor: "actual_net_margin",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_net_margin,
      },
      {
        Header: "Proposed Net Margin Percentage",
        accessor: "proposed_net_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_net_margin_percentage,
      },
      {
        Header: "Actual Net Margin Percentage",
        accessor: "actual_net_margin_percentage",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_net_margin_percentage,
      },
      {
        Header: "Proposed Overall Total",
        accessor: "proposed_overall_total",
        Cell: ({ value }) => formatCurrency(value) ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposed_overall_total,
      },
      {
        Header: "Actual Overall Total",
        accessor: "actual_overall_total",
        Cell: ({ value }) => formatCurrency(value) ?? "",
        disableSortBy: true,
        visible: columnVisibility.actual_overall_total,
      },
      {
        Header: "Job Address",
        accessor: "job_address_line_1",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_stage_address,
      },
      {
        Header: "Job City",
        accessor: "job_address_city",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_stage_city,
      },
      {
        Header: "Job State",
        accessor: "job_address_state",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_stage_state,
      },
      {
        Header: "Job Zip",
        accessor: "job_address_zip",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_stage_zip,
      },
      {
        Header: "Crew Name",
        accessor: "crew_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.crew_name,
      },
      {
        Header: "Crew Display Name",
        accessor: "crew_display_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.crew_display_name,
      },
      {
        Header: "Product Name",
        accessor: "proposal_product_name",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.product_name,
      },
      {
        Header: "Product Job Size",
        accessor: "proposal_product_job_size",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_product_job_size,
      },
      {
        Header: "Product Job Duration",
        accessor: "proposal_product_job_duration",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_product_job_duration,
      },
      {
        Header: "Product Job Duration",
        accessor: "proposal_product_job_duration",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_product_material_duration,
      },
      {
        Header: "Product Material Type",
        accessor: "proposal_product_material_type",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_product_material_type,
      },
      {
        Header: "Product Material Used (From Job Costing)",
        accessor: "proposal_product_material_used",
        Cell: ({ value }) => value ?? "",
        disableSortBy: true,
        visible: columnVisibility.proposal_product_material_used,
      },
    ];
  }, [columnVisibility]);

  // Create the filtered table data structure for column visibility
  const filteredTableData = useMemo(() => {
    return {
      columns: columns.filter((column) => column.visible),
      rows: tableData?.rows || [],
    };
  }, [columns, tableData]);

  const [requestData, setRequestData] = useState<GenerateReportMutationVariables | null>(null);

  const downloadFileRef = useRef(null);

  useEffect(() => {
    if (generateReportData?.generateReport) {
      downloadFileRef.current.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURI(
        generateReportData.generateReport
      )}`;
      downloadFileRef.current.download = `bitumio-${kebabCase(requestData.type)}-report.xlsx`;
      downloadFileRef.current.click();
    }
  }, [generateReportData]);

  const changeDate = async (selectedDate: Array<Date>, type) => {
    const formattedDate = dayjs(selectedDate[0]).format("YYYY-MM-DD 00:00:00");
    setDate((date) => ({ ...date, [type]: formattedDate }));
  };

  const onSubmit = (values) => {
    // Trigger backend pagination search with current date values
    getReportJobDetails({ page: 1, first: 25 });
  };

  const handleDownload = (e) => {
    const variables = { type: ReportType.JOB_DETAILS, ...date, companyId, jobId };
    setRequestData(variables);

    // Use separate hook for downloads that shows success message
    getReportJobDetailsForDownload({ variables });
    generateReport({ variables });
  };

  useEffect(() => {
    if (companyId) {
      getCompany({ id: companyId });
    }
  }, [getCompany, companyId]);

  // Sync companyName with selected company
  useEffect(() => {
    if (company?.name) {
      setCompanyName(company.name);
    } else if (companyId === null || companyId === undefined) {
      setCompanyName("");
    }
  }, [company, companyId]);

  // Format dates for display
  const formatDisplayDate = (dateString: string) => {
    return dayjs(dateString).format("MMM D, YYYY");
  };

  const handleColumnVisibilityChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        "& .css-1h9h5al-MuiAutocomplete-root .MuiOutlinedInput-root": {
          padding: "5px !important",
        },
      }}
    >
      <DashboardPage>
        <FormProvider {...methods}>
          <MDBox mb={6} component="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3} alignItems="center" display="flex" justifyContent="center">
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Card>
                  <MDBox
                    p={3}
                    lineHeight={1}
                    display="flex"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-between"
                  >
                    <Box>
                      <MDTypography variant="h4" fontWeight="medium">
                        {capitalCase(ReportType.JOB_DETAILS)} Report
                      </MDTypography>
                      <MDTypography variant="body2" color="text" mt={1}>
                        Access detailed job information and progress reports
                      </MDTypography>
                    </Box>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "info.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon sx={{ color: "#fff" }}>engineering</Icon>
                    </Box>
                  </MDBox>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <MDBox p={3}>
                    <MDTypography variant="h6" mb={2}>
                      Filter Options
                    </MDTypography>
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={6} md={3} lg={3}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Start Date
                          </MDTypography>
                          <MDDatePicker
                            defaultValue={startDateFormatted}
                            onChange={(val) => changeDate(val, "startDate")}
                            fullWidth
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={3} lg={3}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            End Date
                          </MDTypography>
                          <MDDatePicker
                            defaultValue={today}
                            onChange={(val) => changeDate(val, "endDate")}
                            fullWidth
                          />
                        </MDBox>
                      </Grid>
                      <Grid item xs={6} md={2.5} lg={2.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Client
                          </MDTypography>
                          <CompanyOptions
                            triggerRefetch={triggerRefetch}
                            selectedCompany={company}
                            setTriggerRefetch={(trigger) => setTriggerRefetch(trigger)}
                          >
                            {({ companyOptions, loading, search, pagination }) => (
                              <Controller
                                name="companyId"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                  <>
                                    <ScrollAsyncAutocomplete
                                      {...field}
                                      value={
                                        companyOptions?.find((x) => x.value === field.value) || null
                                      }
                                      options={companyOptions ?? []}
                                      loading={loading}
                                      label="Client"
                                      search={search}
                                      onLoadMore={pagination.loadMore}
                                      hasMore={pagination.hasNextPage}
                                      onChange={(value) => {
                                        field.onChange(value || null);
                                      }}
                                    />
                                  </>
                                )}
                              />
                            )}
                          </CompanyOptions>
                        </MDBox>
                      </Grid>
                      {/* <Grid item xs={6} md={2} lg={2}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDTypography variant="body2" fontWeight="regular" mb={1}>
                            Job
                          </MDTypography>
                          <JobOptions>
                            {({ jobOptions, loading, search }) => (
                              <Controller
                                name="jobId"
                                control={methods.control}
                                render={({ field, fieldState: { error: fieldError } }) => (
                                  <ScrollAsyncAutocomplete
                                    {...field}
                                    value={jobOptions?.find((x) => x.value === field.value) || null}
                                    options={jobOptions ?? []}
                                    loading={loading}
                                    label="Job"
                                    search={search}
                                    onLoadMore={() => {}}
                                    hasMore={false}
                                    onChange={(value) => {
                                      field.onChange(value || null);
                                      const newJobId = value ? String(value) : "";
                                      setJobId(newJobId);
                                      // Set job name for display and clear when filter is cleared
                                      if (value) {
                                        const selectedOption = jobOptions?.find(
                                          (x) => x.value === value
                                        );
                                        setJobName(selectedOption?.label || "");
                                      } else {
                                        setJobName("");
                                      }
                                    }}
                                  />
                                )}
                              />
                            )}
                          </JobOptions>
                        </MDBox>
                      </Grid> */}
                      <Grid item xs={4} md={1.5} lg={1.5}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <a ref={downloadFileRef} className="d-none" />
                          <MDButton color="success" type="submit" fullWidth sx={{ height: "40px" }}>
                            RUN REPORT
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={4} md={1} lg={1}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDButton
                            color="info"
                            onClick={handleDownload}
                            fullWidth
                            sx={{ height: "40px" }}
                          >
                            <Icon fontSize="small" sx={{ mr: 0.5 }}>
                              download
                            </Icon>
                          </MDButton>
                        </MDBox>
                      </Grid>
                      <Grid item xs={4} md={1} lg={1}>
                        <MDBox
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-end"
                        >
                          <MDButton
                            id="columns-visibility"
                            color="info"
                            onClick={handleMenuOpen}
                            fullWidth
                            sx={{ height: "40px" }}
                          >
                            <Icon fontSize="small" sx={{ mr: 0.5 }}>
                              visibility
                            </Icon>
                          </MDButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                              style: {
                                maxHeight: 400,
                                minWidth: 250,
                              },
                            }}
                          >
                            {columns.map((column) => (
                              <MenuItem
                                key={column.accessor}
                                onClick={() => handleColumnVisibilityChange(column.accessor)}
                              >
                                <Checkbox
                                  checked={columnVisibility[column.accessor]}
                                  onChange={() => handleColumnVisibilityChange(column.accessor)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <ListItemText primary={column.Header} />
                              </MenuItem>
                            ))}
                          </Menu>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Grid container alignItems="center">
                <Grid item xs>
                  <MDTypography variant="h6" color="text">
                    Report Results
                  </MDTypography>
                </Grid>
                <Grid item>
                  <MDTypography variant="body2" color="text">
                    {companyName ? `Client: ${companyName}, ` : ""}
                    {jobName ? `Job: ${jobName}, ` : ""}
                    {date.startDate && date.endDate ? (
                      <>
                        Period: {formatDisplayDate(date.startDate)} -{" "}
                        {formatDisplayDate(date.endDate)}
                      </>
                    ) : (
                      "Select date range"
                    )}
                  </MDTypography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1.5 }} />
            </Box>

            <Grid container display="flex">
              <>
                <DataTable
                  canSearch={false}
                  table={filteredTableData}
                  showTotalEntries={true}
                  isSorted={true}
                  pagination={pagination}
                  paginationProps={{ variant: "gradient", color: "info" }}
                  entriesPerPageOptions={["25", "50"]}
                />
              </>
            </Grid>
          </MDBox>
        </FormProvider>
      </DashboardPage>
    </Box>
  );
};

export default ReportJobDetails;
